import { Drawer, ModalHeader } from '@starsoft/common/components';
import styles from './styles.module.scss';
import SecurityDrawerContent from './Content';
import { SecurityDrawerProps } from './props';
import { useTranslation } from '../../../../../../hooks';

/**
 * SecurityDrawer Component
 * A drawer component that handles security verification UI for withdrawals.
 *
 * Constants & Hooks:
 * - t: Translation function from useTranslation hook, scoped to 'withdraw' namespace
 *
 * Logic scope:
 * - Provides a drawer interface for security verification
 * - Handles drawer visibility through open/close states
 * - Manages loading states for verification process
 * - Integrates translation support for internationalization
 * - Renders a modal header with title and close button
 * - Contains security verification content in a scrollable container
 *
 * Component Structure:
 * - Drawer (root container)
 *   - Container div
 *     - ModalHeader (title and close button)
 *     - Content container
 *       - Divider
 *       - SecurityDrawerContent (verification UI)
 *
 * @component
 * @param {Object} props - Component props
 * @param {() => void} props.onClose - Callback function triggered when drawer should close
 * @param {boolean} props.open - Controls drawer visibility state
 * @param {boolean} props.isLoading - Loading state for verification process
 * @returns {JSX.Element} A drawer component containing security verification UI
 *
 * @example
 * // Usage example:
 * const handleClose = () => function('drawer closed');
 * const isOpen = true;
 * const loading = false;
 *
 * <SecurityDrawer
 *   onClose={handleClose}
 *   open={isOpen}
 *   isLoading={loading}
 * />
 */
export default function SecurityDrawer({
  onClose,
  open,
  isLoading,
}: SecurityDrawerProps) {
  const { t } = useTranslation('withdraw');

  return (
    <Drawer open={open} onClose={onClose} className={styles.drawer} absolute>
      <div className={styles.container}>
        <ModalHeader
          smallPadding
          title={t('withdraw_verification')}
          onClose={onClose}
        />
        <div className={styles.container__content}>
          <div className={styles.container__divider} />
          <SecurityDrawerContent isLoading={isLoading} />
        </div>
      </div>
    </Drawer>
  );
}
